/**
 * WebRTC Hook for Hayat Family Video Calls
 * 
 * Manages RTCPeerConnection, media streams, and signaling
 * for 1:1 video calls between Family and Device apps
 */
import { useState, useEffect, useRef, useCallback } from 'react';

// STUN server configuration
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]
};

export type CallRole = 'caller' | 'callee';
export type WebRTCState = 'idle' | 'initializing' | 'connecting' | 'connected' | 'failed' | 'closed';

export interface WebRTCSignal {
  type: 'offer' | 'answer' | 'candidate';
  data: any;
}

export interface UseWebRTCOptions {
  callId: string;
  role: CallRole;
  onSignal: (signal: WebRTCSignal) => void;
  onError?: (error: Error) => void;
  onStateChange?: (state: WebRTCState) => void;
}

export function useWebRTC(options: UseWebRTCOptions | null) {
  const { callId, role, onSignal, onError, onStateChange } = options || {};
  
  // State
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [webrtcState, setWebrtcState] = useState<WebRTCState>('idle');
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  
  // Refs
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const iceCandidateQueueRef = useRef<RTCIceCandidate[]>([]);
  
  /**
   * Update state and notify parent
   */
  const updateState = useCallback((newState: WebRTCState) => {
    console.log(`[WebRTC] State: ${webrtcState} → ${newState}`);
    setWebrtcState(newState);
    onStateChange?.(newState);
  }, [webrtcState, onStateChange]);
  
  /**
   * Initialize local media stream
   */
  const initializeMedia = useCallback(async (): Promise<MediaStream> => {
    console.log('[WebRTC] Requesting media access...');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      });
      
      console.log('[WebRTC] ✅ Media acquired:', {
        audioTracks: stream.getAudioTracks().length,
        videoTracks: stream.getVideoTracks().length
      });
      
      localStreamRef.current = stream;
      setLocalStream(stream);
      
      return stream;
    } catch (error) {
      console.error('[WebRTC] ❌ Media access denied:', error);
      const err = new Error(`Failed to access camera/microphone: ${error}`);
      onError?.(err);
      throw err;
    }
  }, [onError]);
  
  /**
   * Create RTCPeerConnection
   */
  const createPeerConnection = useCallback((): RTCPeerConnection => {
    console.log('[WebRTC] Creating peer connection...');
    
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[WebRTC] 🧊 Sending ICE candidate');
        onSignal?.({
          type: 'candidate',
          data: event.candidate.toJSON()
        });
      }
    };
    
    // Handle ICE connection state changes
    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE connection state:', pc.iceConnectionState);
      
      if (pc.iceConnectionState === 'connected') {
        updateState('connected');
      } else if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        updateState('failed');
      } else if (pc.iceConnectionState === 'closed') {
        updateState('closed');
      }
    };
    
    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log('[WebRTC] Connection state:', pc.connectionState);
      
      if (pc.connectionState === 'connected') {
        updateState('connected');
      } else if (pc.connectionState === 'failed') {
        updateState('failed');
        onError?.(new Error('WebRTC connection failed'));
      } else if (pc.connectionState === 'closed') {
        updateState('closed');
      }
    };
    
    // Handle incoming tracks (remote stream)
    pc.ontrack = (event) => {
      console.log('[WebRTC] 📹 Received remote track:', event.track.kind);
      
      if (event.streams && event.streams[0]) {
        remoteStreamRef.current = event.streams[0];
        setRemoteStream(event.streams[0]);
      }
    };
    
    peerConnectionRef.current = pc;
    return pc;
  }, [onSignal, onError, updateState]);
  
  /**
   * Start call as caller (Family side)
   */
  const startCall = useCallback(async () => {
    if (!callId || role !== 'caller') {
      console.warn('[WebRTC] Cannot start call: missing callId or not caller');
      return;
    }
    
    console.log('[WebRTC] 🚀 Starting call as CALLER');
    updateState('initializing');
    
    try {
      // Get local media
      const stream = await initializeMedia();
      
      // Create peer connection
      const pc = createPeerConnection();
      
      // Add local tracks to peer connection
      stream.getTracks().forEach(track => {
        console.log('[WebRTC] Adding local track:', track.kind);
        pc.addTrack(track, stream);
      });
      
      updateState('connecting');
      
      // Create offer
      console.log('[WebRTC] Creating offer...');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      
      await pc.setLocalDescription(offer);
      console.log('[WebRTC] ✅ Offer created and set as local description');
      
      // Send offer via signaling
      onSignal?.({
        type: 'offer',
        data: pc.localDescription?.toJSON()
      });
      
      console.log('[WebRTC] 📤 Offer sent via signaling');
      
    } catch (error) {
      console.error('[WebRTC] ❌ Failed to start call:', error);
      updateState('failed');
      onError?.(error as Error);
    }
  }, [callId, role, initializeMedia, createPeerConnection, onSignal, onError, updateState]);
  
  /**
   * Accept call as callee (Device side)
   */
  const acceptCall = useCallback(async (offer: RTCSessionDescriptionInit) => {
    if (!callId || role !== 'callee') {
      console.warn('[WebRTC] Cannot accept call: missing callId or not callee');
      return;
    }
    
    console.log('[WebRTC] 📞 Accepting call as CALLEE');
    updateState('initializing');
    
    try {
      // Get local media
      const stream = await initializeMedia();
      
      // Create peer connection
      const pc = createPeerConnection();
      
      // Add local tracks
      stream.getTracks().forEach(track => {
        console.log('[WebRTC] Adding local track:', track.kind);
        pc.addTrack(track, stream);
      });
      
      updateState('connecting');
      
      // Set remote description (offer)
      console.log('[WebRTC] Setting remote offer...');
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      
      // Process queued ICE candidates
      if (iceCandidateQueueRef.current.length > 0) {
        console.log(`[WebRTC] Processing ${iceCandidateQueueRef.current.length} queued ICE candidates`);
        for (const candidate of iceCandidateQueueRef.current) {
          await pc.addIceCandidate(candidate);
        }
        iceCandidateQueueRef.current = [];
      }
      
      // Create answer
      console.log('[WebRTC] Creating answer...');
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      console.log('[WebRTC] ✅ Answer created and set as local description');
      
      // Send answer via signaling
      onSignal?.({
        type: 'answer',
        data: pc.localDescription?.toJSON()
      });
      
      console.log('[WebRTC] 📤 Answer sent via signaling');
      
    } catch (error) {
      console.error('[WebRTC] ❌ Failed to accept call:', error);
      updateState('failed');
      onError?.(error as Error);
    }
  }, [callId, role, initializeMedia, createPeerConnection, onSignal, onError, updateState]);
  
  /**
   * Handle incoming signaling message
   */
  const handleSignal = useCallback(async (signal: WebRTCSignal) => {
    const pc = peerConnectionRef.current;
    
    if (!pc) {
      console.warn('[WebRTC] No peer connection, ignoring signal');
      return;
    }
    
    try {
      if (signal.type === 'answer') {
        console.log('[WebRTC] 📥 Received answer');
        await pc.setRemoteDescription(new RTCSessionDescription(signal.data));
        
        // Process queued ICE candidates
        if (iceCandidateQueueRef.current.length > 0) {
          console.log(`[WebRTC] Processing ${iceCandidateQueueRef.current.length} queued ICE candidates`);
          for (const candidate of iceCandidateQueueRef.current) {
            await pc.addIceCandidate(candidate);
          }
          iceCandidateQueueRef.current = [];
        }
        
      } else if (signal.type === 'candidate') {
        console.log('[WebRTC] 🧊 Received ICE candidate');
        
        const candidate = new RTCIceCandidate(signal.data);
        
        // If remote description is set, add candidate immediately
        if (pc.remoteDescription && pc.remoteDescription.type) {
          await pc.addIceCandidate(candidate);
        } else {
          // Queue candidate until remote description is set
          console.log('[WebRTC] Queuing ICE candidate (no remote description yet)');
          iceCandidateQueueRef.current.push(candidate);
        }
      }
    } catch (error) {
      console.error('[WebRTC] ❌ Error handling signal:', error);
      onError?.(error as Error);
    }
  }, [onError]);
  
  /**
   * Toggle audio
   */
  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
        console.log('[WebRTC] Audio:', audioTrack.enabled ? 'ON' : 'OFF');
      }
    }
  }, []);
  
  /**
   * Toggle video
   */
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
        console.log('[WebRTC] Video:', videoTrack.enabled ? 'ON' : 'OFF');
      }
    }
  }, []);
  
  /**
   * End call and cleanup
   */
  const endCall = useCallback(() => {
    console.log('[WebRTC] 🛑 Ending call and cleaning up...');
    
    // Stop all local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('[WebRTC] Stopped local track:', track.kind);
      });
      localStreamRef.current = null;
      setLocalStream(null);
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Clear remote stream
    remoteStreamRef.current = null;
    setRemoteStream(null);
    
    // Clear ICE candidate queue
    iceCandidateQueueRef.current = [];
    
    updateState('closed');
    console.log('[WebRTC] ✅ Cleanup complete');
  }, [updateState]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endCall();
    };
  }, [endCall]);
  
  return {
    // State
    localStream,
    remoteStream,
    webrtcState,
    isAudioEnabled,
    isVideoEnabled,
    
    // Actions
    startCall,
    acceptCall,
    handleSignal,
    toggleAudio,
    toggleVideo,
    endCall,
  };
}
